import {
    useEffect,
    useState,
    forwardRef,
    useImperativeHandle,
    useMemo,
} from 'react';
import _ from 'lodash';
import { useWorkOrderPhotoTypesQuery } from 'common/stores/redux/MetaStore/MetaStoreApi';
import { WorkOrderPhotoTypeAPI } from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import { Photo } from 'common/types/documentRequirements';
import TypeAheadSearchBar from 'common/components/form/TypeAheadSearchBar';

interface DocumentRequirementsPhotoSearchProps {
    mediaList: Photo[];
    disabled: boolean;
    onSearchChanged: (
        selectedTypeList: (WorkOrderPhotoTypeAPI | undefined)[] | undefined
    ) => void;
}

const DocumentRequirementsPhotoSearch = forwardRef(
    (
        {
            mediaList,
            disabled = false,
            onSearchChanged,
        }: DocumentRequirementsPhotoSearchProps,
        ref
    ) => {
        const { data: workOrderPhotoTypes } = useWorkOrderPhotoTypesQuery();
        const [selectedTypeList, setSelectedTypeList] =
            useState<(WorkOrderPhotoTypeAPI | undefined)[]>();

        const clearSelectedTypeList = () => {
            setSelectedTypeList([]);
        };

        const removeSelectedTypeList = (id: string) => {
            const newList = _.reject(selectedTypeList, x => x?.id === id);
            setSelectedTypeList(newList);
        };

        useImperativeHandle(ref, () => ({
            clearSelectedTypeList,
            removeSelectedTypeList,
        }));

        const searchOptions = useMemo(() => {
            return _.chain(mediaList)
                .map(x => x.requirement.photoTypeId)
                .reject(x => _.some(selectedTypeList, y => y?.id === x))
                .uniq()
                .map(x => workOrderPhotoTypes?.find(y => y.id === x))
                .reject(x => _.isNil(x))
                .sort()
                .value() as WorkOrderPhotoTypeAPI[];
        }, [mediaList, selectedTypeList, workOrderPhotoTypes]);

        useEffect(() => {
            if (onSearchChanged) {
                onSearchChanged(selectedTypeList);
            }
        }, [selectedTypeList, onSearchChanged]);

        const handleOnSelect = (item: WorkOrderPhotoTypeAPI) => {
            setSelectedTypeList(prev => [...(prev ?? []), item]);
        };

        return (
            <TypeAheadSearchBar
                options={searchOptions}
                onChange={handleOnSelect}
                disabled={disabled}
                noOptionsText='No results found'
            />
        );
    }
);

export default DocumentRequirementsPhotoSearch;
